import { X } from 'lucide-react';
import { forwardRef, useImperativeHandle, useState } from 'react';
import { Button } from '../../ui/Button/Button';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '../../ui/Sheet/Sheet';

interface Props {
  btnTitle: string;
  btnStyle?: string;
  barTitle?: string;
  barTitleStyle?: string;
  children?: React.ReactNode;
  icon?: React.ReactNode;
  onOpenChange?: (open: boolean) => void;
  open?: boolean;
  onHandelClick?: () => void;
  isTableRow?: boolean;
}

export interface MainRightBarRef {
  setOpen: (open: boolean) => void;
  close: () => void; // Add close method
}

const MainRightBar = forwardRef<MainRightBarRef, Props>(
  (
    {
      btnStyle,
      btnTitle,
      barTitle,
      icon,
      barTitleStyle,
      children,
      onOpenChange,
      open: controlledOpen,
      onHandelClick,
      isTableRow = false,
    },
    ref,
  ) => {
    const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
    const isControlled = controlledOpen !== undefined;
    const open = isControlled ? controlledOpen : uncontrolledOpen;

    const handleOpenChange = (newOpen: boolean) => {
      if (!isControlled) {
        setUncontrolledOpen(newOpen);
      }
      onOpenChange?.(newOpen);
    };

    if (isTableRow) {
      useImperativeHandle(ref, () => ({
        setOpen: (newOpen: boolean) => handleOpenChange(newOpen),
        close: () => handleOpenChange(false), // Expose close method
      }));
    }

    const handleTriggerClick = () => {
      if (onHandelClick) onHandelClick();
      if (!open) handleOpenChange(true);
    };

    return (
      <Sheet open={open} onOpenChange={handleOpenChange}>
        <SheetTrigger asChild>
          <Button className={btnStyle} onClick={handleTriggerClick}>
            {icon} {btnTitle}
          </Button>
        </SheetTrigger>
        <SheetContent className="p-8 bg-white">
          <Button
            className="absolute end-2 top-2 h-8 w-8 p-0 border-none !bg-transparent"
            onClick={() => handleOpenChange(false)}>
            <X className="w-5 h-5" color="#DFE3E8" />
          </Button>
          <SheetHeader>
            <SheetTitle className={barTitleStyle}>{barTitle}</SheetTitle>
            <SheetDescription></SheetDescription>
          </SheetHeader>
          {children}
        </SheetContent>
      </Sheet>
    );
  },
);

MainRightBar.displayName = 'MainRightBar';

export default MainRightBar;
